import React, { useEffect, useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { setUpdateUsersList } from 'reducers/misc';
import { UsersApi } from '../../api/usersApi';
import useFetchUserDetails from './hooks/useFetchUserDetails';
import { ISubscriptionItem, IUser } from './models';
import { PopupOrPanel } from '../../components/popupsAndPanels/PopupAndPanels';
import { FormBooleanField } from 'components/formComponents/FormBooleanField';
import FormTextField from 'components/formComponents/FormTextField';
import Tooltip from 'components/tooltip/Tooltip';
import FormGeneralError from 'components/formComponents/FormGeneralError';
import ErrorMessage from 'components/errorMessage/ErrorMessage';
import { ERROR_MESSAGE } from 'appConstants';
import { useGetDashboardList } from 'pages/dashboard/useGetDashboardList';
import { Spinner } from 'components/spinner/Spinner';

type Props = {
  panelOpen: boolean;
  togglePanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  id: number;
};

const tooltip = (
  <Tooltip text="This person will have access to subscriptions, manage users and manage school" />
);

export const UpdateUser: React.FC<Props> = ({
  panelOpen,
  togglePanelOpen,
  id,
}) => {
  const [initialValues, setInitialValues] = useState<IUser | null>(null);
  const [error, setError] = useState<string>('');
  const [detailsError, setDetailsError] = useState<string>('');

  const dispatch = useDispatch();
  const setUpdateList = (status: boolean) =>
    dispatch(setUpdateUsersList(status));

  useFetchUserDetails('update', id, setInitialValues, setDetailsError);

  const validationSchemaAddUser = Yup.object({
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    emailAddress: Yup.string()
      .email('Please enter a valid email address')
      .required('Email address is required'),
    bMainAccountHolder: Yup.boolean().required('Required field'),
    bGetSet4PE: Yup.boolean().required('Required field'),
    bGetSet4Music: Yup.boolean().required('Required field'),
  });

  const { dashboardListInfo, error: subscriptionsListError } =
    useGetDashboardList();

  const [subscriptionsList, setSubscriptionList] = useState<
    ISubscriptionItem[] | null
  >(null);

  useEffect(() => {
    if (dashboardListInfo) {
      const subjectsList = dashboardListInfo.map((item) => ({
        label: item.subject,
        fieldName: `b${item.subject.split(' ').join('')}`,
      }));
      setSubscriptionList(subjectsList);
    }
  }, [dashboardListInfo]);

  const handleSubmitUserDetails = async (
    values: IUser,
    { setSubmitting }: FormikHelpers<IUser>
  ) => {
    setError('');
    setSubmitting(true);
    const result = await UsersApi.submitUser(id, values);
    if (result.data) {
      const paramItems = JSON.stringify(localStorage.getItem("updateItemTableParams") || '{}');
      setSubmitting(false);
      setUpdateList(true);
      togglePanelOpen(false);
    }

    if (result.error) {
      const error = result.error.message;
      setError(error);
      setSubmitting(false);
    }
  };

  if (detailsError)
    return (
      <PopupOrPanel
        isPopup={false}
        isOpen={panelOpen}
        hdrTitle={'Update user'}
        position="right"
        primaryBtnTxt="Close"
        secondaryBtnTxt="Cancel"
        givenClass="UpdateManagedUser"
        handleClose={() => togglePanelOpen(false)}
        handlePrimaryBtn={() => togglePanelOpen(false)}
        handleSecondaryBtn={() => togglePanelOpen(false)}
        width={560}
      >
        <ErrorMessage message={ERROR_MESSAGE} />
      </PopupOrPanel>
    );

  if (initialValues)
    return (
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchemaAddUser}
        onSubmit={(values, formikHelpers) =>
          handleSubmitUserDetails(values, formikHelpers)
        }
      >
        {({ values, submitForm, setFieldValue, isSubmitting }) => (
          <PopupOrPanel
            isPopup={false}
            isOpen={panelOpen}
            hdrTitle={'Update user'}
            position="right"
            primaryBtnTxt="Update"
            secondaryBtnTxt="Cancel"
            givenClass="UpdateManagedUser"
            handleClose={() => togglePanelOpen(false)}
            handlePrimaryBtn={submitForm}
            handleSecondaryBtn={() => togglePanelOpen(false)}
            width={560}
            isSubmitting={isSubmitting}
          >
            {subscriptionsList ? (
              <Form>
                {error && <FormGeneralError message={error} />}
                <FormTextField
                  label="First name"
                  field="firstName"
                  labelPosition="left"
                  required={true}
                  optionalTag={'optional'}
                  value={values.firstName}
                />
                <FormTextField
                  label="Last name"
                  field="lastName"
                  labelPosition="left"
                  required={true}
                  optionalTag={'optional'}
                  value={values.lastName}
                />
                <FormTextField
                  label="Email address"
                  field="emailAddress"
                  labelPosition="left"
                  required={true}
                  optionalTag={'optional'}
                  value={values.emailAddress}
                />
                <FormBooleanField
                  fieldName="bMainAccountHolder"
                  label="Main account holder"
                  labelPosition="left"
                  tooltip={tooltip}
                />
                <h4 className="SpaceTopBtm">Platform access</h4>
                {subscriptionsList.map((item: any, index: number) => (
                  <FormBooleanField
                    key={index}
                    fieldName={item.fieldName}
                    label={item.label}
                    labelPosition="left"
                  />
                ))}
              </Form>
            ) : (
              <Spinner positionAbsolute />
            )}
          </PopupOrPanel>
        )}
      </Formik>
    );
  return null;
};
